package fr.manyverse.app.presentation.components.player_screen.character

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.MenuBook
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.Shield
import androidx.compose.material.icons.filled.Whatshot
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.CharacteristicEntry
import fr.manyverse.app.domain.model.GaugeEntry
import fr.manyverse.app.presentation.components.character_sheet.CharacterSheetEditor
import fr.manyverse.app.presentation.components.character_sheet.ExpandableDescription
import fr.manyverse.app.presentation.components.character_sheet.rememberCharacterSheetEditorState
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.TableCharacterViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.character_sheet_characteristics_section
import manyverse.shared.generated.resources.character_sheet_done
import manyverse.shared.generated.resources.character_sheet_edit
import manyverse.shared.generated.resources.character_sheet_empty
import manyverse.shared.generated.resources.character_sheet_fix_errors
import manyverse.shared.generated.resources.character_sheet_gauges_section
import manyverse.shared.generated.resources.character_sheet_powers_section
import manyverse.shared.generated.resources.character_sheet_skills_section
import org.jetbrains.compose.resources.stringResource

@Composable
fun CharacterSheetTab(
    character: Character,
    viewModel: TableCharacterViewModel,
    modifier: Modifier = Modifier
) {
    val updateState by viewModel.updateSheetState.collectAsStateWithLifecycle()
    var editMode by remember { mutableStateOf(false) }
    val sheet = character.sheet

    // Brouillon d'édition, réinitialisé depuis la fiche courante à chaque entrée en mode édition.
    val editorState = rememberCharacterSheetEditorState(
        initial = sheet,
        resetKey = "$editMode-${character.id}"
    )

    Column(modifier = modifier.fillMaxSize()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.End
        ) {
            if (updateState is AsyncUiState.Loading) {
                CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
            }
            if (editMode) {
                Button(onClick = {
                    editorState.validateAndBuild()?.let { built ->
                        viewModel.saveSheet(character, built)
                        editMode = false
                    }
                }) {
                    Icon(Icons.Default.Check, contentDescription = null, modifier = Modifier.size(18.dp))
                    Text(
                        text = stringResource(Res.string.character_sheet_done),
                        modifier = Modifier.padding(start = 8.dp)
                    )
                }
            } else {
                OutlinedButton(onClick = { editMode = true }) {
                    Icon(Icons.Default.Edit, contentDescription = null, modifier = Modifier.size(18.dp))
                    Text(
                        text = stringResource(Res.string.character_sheet_edit),
                        modifier = Modifier.padding(start = 8.dp)
                    )
                }
            }
        }

        if (editMode && editorState.showErrors) {
            Text(
                text = stringResource(Res.string.character_sheet_fix_errors),
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.padding(horizontal = 16.dp)
            )
        }
        (updateState as? AsyncUiState.Error)?.let {
            Text(
                text = it.message,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.padding(horizontal = 16.dp)
            )
        }

        if (editMode) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(start = 16.dp, end = 16.dp, top = 8.dp, bottom = 96.dp)
            ) {
                CharacterSheetEditor(state = editorState)
            }
            return@Column
        }

        val isEmpty = sheet.gauges.isEmpty() &&
            sheet.characteristics.isEmpty() &&
            sheet.skills.isEmpty() &&
            sheet.powers.isEmpty()

        if (isEmpty) {
            Text(
                text = stringResource(Res.string.character_sheet_empty),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.padding(horizontal = 16.dp, vertical = 24.dp)
            )
            return@Column
        }

        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(start = 16.dp, end = 16.dp, top = 8.dp, bottom = 96.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            if (sheet.gauges.isNotEmpty()) {
                SheetSection(
                    icon = Icons.Default.Favorite,
                    title = stringResource(Res.string.character_sheet_gauges_section)
                ) {
                    sheet.gauges.forEach { gauge ->
                        GaugeRow(
                            gauge = gauge,
                            onIncrement = { viewModel.incrementGauge(character, gauge.id) },
                            onDecrement = { viewModel.decrementGauge(character, gauge.id) }
                        )
                    }
                }
            }

            if (sheet.characteristics.isNotEmpty()) {
                SheetSection(
                    icon = Icons.Default.Shield,
                    title = stringResource(Res.string.character_sheet_characteristics_section)
                ) {
                    @OptIn(ExperimentalLayoutApi::class)
                    FlowRow(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        sheet.characteristics.forEach { characteristic ->
                            CharacteristicCell(
                                characteristic = characteristic,
                                modifier = Modifier.widthIn(min = 140.dp).weight(1f)
                            )
                        }
                    }
                }
            }

            if (sheet.skills.isNotEmpty()) {
                SheetSection(
                    icon = Icons.AutoMirrored.Filled.MenuBook,
                    title = stringResource(Res.string.character_sheet_skills_section)
                ) {
                    sheet.skills.forEach { skill ->
                        Column(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
                            Text(
                                text = skill.label,
                                style = MaterialTheme.typography.bodyLarge,
                                modifier = Modifier.fillMaxWidth()
                            )
                            ExpandableDescription(skill.description)
                        }
                    }
                }
            }

            if (sheet.powers.isNotEmpty()) {
                SheetSection(
                    icon = Icons.Default.Whatshot,
                    title = stringResource(Res.string.character_sheet_powers_section)
                ) {
                    sheet.powers.forEach { power ->
                        Column(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
                            Text(
                                text = power.label,
                                style = MaterialTheme.typography.bodyLarge,
                                modifier = Modifier.fillMaxWidth()
                            )
                            ExpandableDescription(power.description)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SheetSection(
    icon: ImageVector,
    title: String,
    content: @Composable ColumnScope.() -> Unit
) {
    OutlinedCard(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp)
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(20.dp)
                )
                Text(
                    text = title.uppercase(),
                    style = MaterialTheme.typography.labelLarge,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = 1.5.sp,
                    color = MaterialTheme.colorScheme.primary
                )
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .height(1.dp)
                        .background(
                            Brush.horizontalGradient(
                                listOf(
                                    MaterialTheme.colorScheme.primary.copy(alpha = 0.3f),
                                    Color.Transparent
                                )
                            )
                        )
                )
            }
            content()
        }
    }
}

@Composable
private fun GaugeRow(
    gauge: GaugeEntry,
    onIncrement: () -> Unit,
    onDecrement: () -> Unit
) {
    val span = gauge.max - gauge.min
    val fraction = if (span > 0) {
        ((gauge.current - gauge.min).toFloat() / span).coerceIn(0f, 1f)
    } else {
        0f
    }
    val gaugeColor = gauge.colorArgb?.let { Color(it) } ?: MaterialTheme.colorScheme.primary

    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                text = gauge.label,
                style = MaterialTheme.typography.bodyLarge,
                modifier = Modifier.weight(1f)
            )
            Row(verticalAlignment = Alignment.Bottom) {
                Text(
                    text = gauge.current.toString(),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Text(
                    text = " / ${gauge.max}",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            IconButton(
                onClick = onDecrement,
                enabled = gauge.current > gauge.min,
                modifier = Modifier.size(32.dp)
            ) {
                Text("−", style = MaterialTheme.typography.titleLarge)
            }
            LinearProgressIndicator(
                progress = { fraction },
                modifier = Modifier
                    .weight(1f)
                    .height(8.dp)
                    .clip(RoundedCornerShape(4.dp)),
                color = gaugeColor,
                trackColor = MaterialTheme.colorScheme.surfaceVariant
            )
            IconButton(
                onClick = onIncrement,
                enabled = gauge.current < gauge.max,
                modifier = Modifier.size(32.dp)
            ) {
                Text("+", style = MaterialTheme.typography.titleLarge)
            }
        }
    }
}

@Composable
private fun CharacteristicCell(
    characteristic: CharacteristicEntry,
    modifier: Modifier = Modifier
) {
    Column(modifier = modifier.padding(vertical = 6.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Text(
                text = characteristic.label,
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onSurface
            )
            DottedLeader(modifier = Modifier.weight(1f))
            Text(
                text = characteristic.value.toString(),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.primary
            )
        }
        ExpandableDescription(characteristic.description)
    }
}

/** Filet de points reliant le libellé à la valeur, comme sur le design. */
@Composable
private fun DottedLeader(modifier: Modifier = Modifier) {
    val color = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
    Box(
        modifier = modifier
            .height(1.dp)
            .drawBehind {
                val y = size.height / 2f
                drawLine(
                    color = color,
                    start = Offset(0f, y),
                    end = Offset(size.width, y),
                    strokeWidth = 1.5.dp.toPx(),
                    cap = StrokeCap.Round,
                    pathEffect = PathEffect.dashPathEffect(
                        floatArrayOf(1f, 6.dp.toPx()),
                        0f
                    )
                )
            }
    )
}
